import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RefreshCw, AlertTriangle, CheckCircle2, UserCheck } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { globalFeatureFlags } from '@/lib/featureFlags';

interface SyncGDrivePermissionsDialogProps {
  projectId: string;
  projectName: string;
  isOpen: boolean;
  onClose: () => void;
}

interface SyncProgress {
  current: number;
  total: number;
  currentUser: string;
}

export const SyncGDrivePermissionsDialog: React.FC<SyncGDrivePermissionsDialogProps> = ({
  projectId,
  projectName,
  isOpen,
  onClose
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<SyncProgress | null>(null);
  const [results, setResults] = useState<{
    success: number;
    failed: number;
    alreadyHasAccess: number;
    alreadyHasAccessUsers: string[];
    errors: string[];
  } | null>(null);

  const handleSync = async () => {
    const flagEnabled = globalFeatureFlags.isEnabled('ENABLE_GDRIVE_PERMISSION_SYNC');
    if (!flagEnabled) {
      toast.error('Google Drive permission sync is currently disabled');
      return;
    }

    setIsLoading(true);
    setProgress(null);
    setResults(null);

    try {
      // Fetch all users with project access via RPC
      const { data: usersData, error: usersError } = await supabase.rpc('get_project_users', {
        p_project_id: projectId
      });

      if (usersError) {
        console.error('Failed to fetch project users:', usersError);
        toast.error('Failed to fetch project users');
        setIsLoading(false);
        return;
      }

      // Type guard and validation
      const users = usersData as Array<{ user_id: string; email: string }> | null;
      if (!users || !Array.isArray(users) || users.length === 0) {
        toast.info('No users found with access to this project');
        setIsLoading(false);
        return;
      }

      const totalUsers = users.length;
      let successCount = 0;
      let errorCount = 0;
      let alreadyHasAccessCount = 0;
      const alreadyHasAccessUsers: string[] = [];
      const errors: string[] = [];

      for (let i = 0; i < users.length; i++) {
        const user = users[i];
        setProgress({
          current: i + 1,
          total: totalUsers,
          currentUser: user.email || user.user_id
        });

        try {
          const { data, error } = await supabase.functions.invoke('sync-gdrive-permissions', {
            body: {
              projectId,
              userId: user.user_id,
              action: 'sync'
            }
          });

          if (error || !data?.success) {
            errorCount++;
            const folderError = data?.errors?.[0]?.error;
            const errorMsg = error?.message || folderError || data?.error?.message || 'Unknown error';
            errors.push(`${user.email || user.user_id}: ${errorMsg}`);
            console.error(`Failed to sync ${user.email}:`, error || data);
          } else {
            // Check if user was skipped (already has access)
            if (data.permissionsSkipped > 0 && data.permissionsGranted === 0) {
              alreadyHasAccessCount++;
              alreadyHasAccessUsers.push(user.email || user.user_id);
            } else {
              successCount++;
            }
          }
        } catch (error: any) {
          errorCount++;
          errors.push(`${user.email || user.user_id}: ${error.message}`);
          console.error(`Error syncing ${user.email}:`, error);
        }
      }

      setResults({
        success: successCount,
        failed: errorCount,
        alreadyHasAccess: alreadyHasAccessCount,
        alreadyHasAccessUsers,
        errors
      });

      if (errorCount === 0) {
        toast.success(`✅ Successfully synced permissions for ${successCount} users`);
      } else if (successCount > 0) {
        toast.warning(`⚠️ Synced ${successCount} users, ${errorCount} failed`);
      } else {
        toast.error(`❌ Failed to sync permissions for all users`);
      }
    } catch (error: any) {
      console.error('Sync error:', error);
      toast.error(`Failed to sync permissions: ${error.message}`);
    } finally {
      setIsLoading(false);
      setProgress(null);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setResults(null);
      setProgress(null);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Sync Google Drive Permissions
          </DialogTitle>
          <DialogDescription>
            Sync folder permissions for all users with access to <strong>{projectName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!isLoading && !results && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This will synchronize Google Drive folder permissions for all users with access to this project.
                The process may take a few moments depending on the number of users.
              </AlertDescription>
            </Alert>
          )}

          {progress && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Syncing permissions...</span>
                <span className="font-medium">{progress.current} / {progress.total}</span>
              </div>
              <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Current user: {progress.currentUser}
              </p>
            </div>
          )}

          {results && (
            <div className="space-y-3">
              {results.success > 0 && (
                <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <div>
                    <p className="text-sm font-medium text-green-900 dark:text-green-100">
                      Successfully synced: {results.success} users
                    </p>
                  </div>
                </div>
              )}

              {results.alreadyHasAccess > 0 && (
                <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <UserCheck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      Already has access: {results.alreadyHasAccess} users
                    </p>
                  </div>
                  <div className="max-h-24 overflow-y-auto space-y-1">
                    {results.alreadyHasAccessUsers.map((user, index) => (
                      <p key={index} className="text-xs text-blue-700 dark:text-blue-300">
                        {user}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {results.failed > 0 && (
                <div className="p-3 bg-red-50 dark:bg-red-950 rounded-lg">
                  <p className="text-sm font-medium text-red-900 dark:text-red-100 mb-2">
                    Failed: {results.failed} users
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {results.errors.map((error, index) => (
                      <p key={index} className="text-xs text-red-700 dark:text-red-300">
                        {error}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {results ? 'Close' : 'Cancel'}
          </Button>
          {!results && (
            <Button
              onClick={handleSync}
              disabled={isLoading}
              className="gap-2"
            >
              {isLoading && <RefreshCw className="h-4 w-4 animate-spin" />}
              {isLoading ? 'Syncing...' : 'Start Sync'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
